import { Alert, Card, Col, Row } from "antd";
import Loading from "components/Loading";
import useRoleAndPermission from "hooks/use-role-and-permission";
import {
  useGetPortfolioLevelHolisticsTokenQuery,
  useLocationQuery,
  useUserWithAuthTokenQuery,
} from "pacts/app-webcore/hasura-webcore.graphql";
import React, { useState } from "react";
import logger from "services/logger/logger";
import { EFeatureFlags } from "utils/constants";

interface LocationListAnalyticsProps {
  embedId?: string;
  filters?: any;
  locationId?: string;
}

const LocationListAnalytics: React.FC<LocationListAnalyticsProps> = ({
  embedId = process.env.REACT_APP_HOLISTICS_LOCATION_LIST_ANALYTICS_EMBED_ID || "576b0b9f6405caae89d8454c",
  filters,
  locationId,
}) => {
  const [holisticsPayload, setHolisticsPayload] = useState<string>("");
  const [accessDenied, setAccessDenied] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const { data: userData } = useUserWithAuthTokenQuery();
  const { isCustomer } = useRoleAndPermission();

  // Determine location ID from props or filters
  const currentLocationId = locationId || filters?.locationId;

  // Query location data to get location-level feature flags
  const { data: locationData } = useLocationQuery({
    variables: { locationId: currentLocationId },
    skip: !currentLocationId, // Skip if no location ID is available
  });

  // Check if user has LocationListAnalytics feature enabled
  const isUserLocationListAnalyticsEnabled = (userData?.userWithAuthToken?.enabledFeatures ?? []).includes(
    EFeatureFlags.LocationListAnalytics
  );

  // Check if location has LocationListAnalytics feature enabled
  const isLocationLocationListAnalyticsEnabled = (locationData?.location?.enabledFeatures ?? []).includes(
    EFeatureFlags.LocationListAnalytics
  );

  // Feature is enabled if either user or location has it enabled
  const isLocationListAnalyticsEnabled = isUserLocationListAnalyticsEnabled || isLocationLocationListAnalyticsEnabled;

  // Check if user is a Hotel Viewer (Customer) with the feature enabled
  const canAccessLocationListAnalytics = isCustomer() && isLocationListAnalyticsEnabled;

  // Always call the hook, but skip if feature is not enabled or user is not a customer
  const {
    loading,
    data: holisticsData,
    error,
  } = useGetPortfolioLevelHolisticsTokenQuery({
    variables: {
      embedId,
      filters: filters ? JSON.stringify(filters) : null,
    },
    skip: !canAccessLocationListAnalytics, // Skip query if feature not enabled or user not a customer
    onCompleted: (completedData) => {
      // Add null/undefined checks to prevent errors
      if (!completedData || !completedData.getPortfolioLevelHolisticsToken) {
        logger.warn("Portfolio level dashboard token is empty or undefined for embedId:", embedId);
        return;
      }

      if (completedData.getPortfolioLevelHolisticsToken === "") {
        logger.warn("Portfolio level dashboard token is empty for embedId:", embedId);
      }
      setHolisticsPayload(completedData.getPortfolioLevelHolisticsToken ?? "");
      setAccessDenied(false);
    },
    onError: (queryError) => {
      logger.error("Error loading portfolio level holistics token:", queryError.message);

      // Check if this is a feature access denied error
      if (queryError.message.includes("Portfolio Level Analytics feature is not allowed for this user")) {
        setAccessDenied(true);
        setErrorMessage(
          "Portfolio Level Analytics is not available for your account. Please contact your administrator."
        );
      } else {
        setErrorMessage("Unable to load analytics. Please try again later.");
      }
    },
  });

  // Get the token from either the state (set by callback) or directly from the hook data
  const currentToken = holisticsPayload || holisticsData?.getPortfolioLevelHolisticsToken;

  // If user is not a customer or feature is not enabled, show access denied
  if (!canAccessLocationListAnalytics) {
    return (
      <div className="location-list-analytics">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card title="Location List Analytics" bordered={false} style={{ width: "100%" }}>
              <Alert
                message="Feature Not Available"
                description="Location List Analytics is not enabled for your account. Please contact your administrator."
                type="warning"
                showIcon
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  if (loading) {
    return <Loading />;
  }

  // Handle error cases - check both state and hook error
  if (error || accessDenied || errorMessage) {
    // Determine if this is an access denied error
    const isAccessDenied =
      accessDenied ||
      (error && error.message.includes("Portfolio Level Analytics feature is not allowed for this user"));
    const displayErrorMessage =
      errorMessage ||
      (error && !isAccessDenied
        ? "Unable to load analytics. Please try again later."
        : "Portfolio Level Analytics is not available for your account. Please contact your administrator.");

    return (
      <div className="location-list-analytics">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card title="Portfolio Level Analytics" bordered={false} style={{ width: "100%" }}>
              <Alert
                message={isAccessDenied ? "Access Denied" : "Error"}
                description={displayErrorMessage}
                type={isAccessDenied ? "warning" : "error"}
                showIcon
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  // Show analytics if token is available
  if (currentToken) {
    return (
      <div className="location-list-analytics">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card title="Portfolio Level Analytics" bordered={false} style={{ width: "100%" }}>
              <iframe
                title="holistics portfolio analytics"
                src={`https://secure.holistics.io/embed/${embedId}?_token=${currentToken}`}
                id="portfolio-analytics-iframe"
                width="100%"
                height="1024px"
                frameBorder="0"
                allowFullScreen
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  // Show empty state if no token available (but we have data, meaning query completed successfully)
  if (holisticsData && !currentToken) {
    return (
      <div className="location-list-analytics">
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Card title="Portfolio Level Analytics" bordered={false} style={{ width: "100%" }}>
              <Alert
                message="No Analytics Available"
                description="Analytics data is not available at this time."
                type="info"
                showIcon
              />
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  // Show empty state if no data available
  return (
    <div className="location-list-analytics">
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Portfolio Level Analytics" bordered={false} style={{ width: "100%" }}>
            <Alert
              message="No Analytics Available"
              description="Analytics data is not available at this time."
              type="info"
              showIcon
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default LocationListAnalytics;
